import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, CheckCircle2, Circle, ClipboardCheck, Package, Plus, ShieldAlert, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { date, moneyShort, relative } from '../lib/format';
import { ACTIVITY_KIND, PHASE_LABEL, SAMPLE_STATUS, SUBMITTAL_STATUS } from '../lib/labels';
import { Button, Card, Empty, ErrorBanner, Loading, PageHeader, StatusBadge, useToast } from '../components/ui';

type Dashboard = {
  newLeads: { id: number; name: string; firm: string | null; updatedAt: string }[];
  tasks: { id: number; title: string; dueDate: string | null; deal: { id: number; name: string } | null; source: string }[];
  /** Circdal only. The API leaves it out for agency users. */
  expiringRegistrations?: { id: number; deal: { id: number; name: string }; expiresAt: string }[];
  submittals: { id: number; title: string; status: string; deal: { id: number; name: string }; updatedAt: string }[];
  samples: { id: number; status: string; deal: { id: number; name: string }; carrier: string | null; trackingNumber: string | null; trackingUrl: string | null; updatedAt: string }[];
  pipeline: { stage: string; count: number; valueCents: number; squareFeet: number }[];
  recentActivity: { id: number; kind: string; subject: string; occurredAt: string; deal: { id: number; name: string } | null; userName: string }[];
  staff: null | { registrations: number; designReview: number; pricingReview: number; samplesOpen: number; samplesFailed: number; photos: number };
};

export default function DashboardPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: ['dashboard'], queryFn: () => api.get<Dashboard>('/dashboard') });
  if (q.isLoading) return <Loading />;
  if (q.error) return <div className="page"><ErrorBanner error={q.error} /></div>;
  const d = q.data!;
  const totalValue = d.pipeline.reduce((s, p) => s + p.valueCents, 0);
  const totalCount = d.pipeline.reduce((s, p) => s + p.count, 0);
  const maxValue = Math.max(1, ...d.pipeline.map((p) => p.valueCents));
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 17 ? 'Good afternoon' : 'Good evening';

  const completeTask = async (id: number) => {
    try {
      await api.patch(`/tasks/${id}`, { done: true });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      toast('Task done.');
    } catch (err) {
      toast(err);
    }
  };

  return (
    <div className="page">
      <PageHeader
        title={`${greeting}, ${user?.firstName}`}
        subtitle={`${totalCount} open project${totalCount === 1 ? '' : 's'} worth ${moneyShort(totalValue)} in your pipeline.`}
        actions={
          <Button variant="primary" icon={<Plus />} onClick={() => navigate('/projects/new')}>
            New project
          </Button>
        }
      />

      {d.staff ? (
        <div className="grid cols-4" style={{ marginBottom: 18 }}>
          <StaffStat to="/staff/registrations" label="Registrations to approve" value={d.staff.registrations} />
          <StaffStat to="/staff/review" label="Designs to review" value={d.staff.designReview} />
          <StaffStat to="/staff/review" label="Packages to price" value={d.staff.pricingReview} />
          <StaffStat to="/staff/samples" label="Sample orders open" value={d.staff.samplesOpen} alert={d.staff.samplesFailed ? `${d.staff.samplesFailed} failed` : undefined} />
        </div>
      ) : null}

      <div className="split">
        <div className="stack loose">
          <Card title="Pipeline by phase" actions={<Link to="/projects" className="btn ghost sm">Open board <ArrowRight size={14} /></Link>}>
            <div className="stack" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
              {d.pipeline.map((p) => (
                <Link key={p.stage} to={`/projects?view=table&stage=${p.stage}`} style={{ textDecoration: 'none', color: 'inherit', display: 'block', minWidth: 0 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(104px, 170px) minmax(0,1fr) auto', gap: 12, alignItems: 'center' }}>
                    <span className="small">{PHASE_LABEL[p.stage]}</span>
                    <div className="progress" style={{ height: 10 }}>
                      <i style={{ width: `${(p.valueCents / maxValue) * 100}%`, background: p.count ? 'var(--emerald)' : 'transparent' }} />
                    </div>
                    <span className="small num" style={{ textAlign: 'right' }}>
                      {p.count} · {moneyShort(p.valueCents)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </Card>

          <Card title="Submittals that need you" flush>
            {d.submittals.length ? (
              <div className="list">
                {d.submittals.map((s) => (
                  <Link key={s.id} className="list-item" to={`/projects/${s.deal.id}/submittals/${s.id}`}>
                    <ClipboardCheck size={18} color="var(--emerald)" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="truncate" style={{ fontWeight: 500 }}>{s.title}</div>
                      <div className="small muted truncate">{s.deal.name}</div>
                    </div>
                    <StatusBadge map={SUBMITTAL_STATUS} value={s.status} />
                  </Link>
                ))}
              </div>
            ) : (
              <Empty title="Nothing waiting on you">Released packages and requested changes show up here.</Empty>
            )}
          </Card>

          <Card title="Recent activity" flush>
            {d.recentActivity.length ? (
              <div className="list">
                {d.recentActivity.map((a) => (
                  <Link key={a.id} className="list-item" to={a.deal ? `/projects/${a.deal.id}/activity` : '#'}>
                    <span className="badge outline" style={{ minWidth: 74, justifyContent: 'center' }}>{ACTIVITY_KIND[a.kind]}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="truncate">{a.subject}</div>
                      <div className="small muted truncate">
                        {a.deal?.name} · {a.userName}
                      </div>
                    </div>
                    <span className="tiny muted">{relative(a.occurredAt)}</span>
                  </Link>
                ))}
              </div>
            ) : (
              <Empty title="No activity yet">Calls, meetings and project updates appear here.</Empty>
            )}
          </Card>
        </div>

        <div className="stack loose">
          {d.newLeads.length ? (
            <Card title="New leads for you" actions={<Link to="/leads?stage=new" className="btn ghost sm">All leads</Link>} flush>
              <div className="list">
                {d.newLeads.map((l) => (
                  <Link key={l.id} to={`/leads?lead=${l.id}`} className="list-item">
                    <Sparkles size={16} color="var(--orange)" style={{ flex: 'none' }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="small truncate" style={{ fontWeight: 500 }}>
                        {l.name}
                      </div>
                      <div className="tiny muted truncate">
                        {l.firm ?? 'No firm yet'} · {relative(l.updatedAt)}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          ) : null}
          <Card title="My tasks" actions={<Link to="/tasks" className="btn ghost sm">All tasks</Link>} flush>
            {d.tasks.length ? (
              <div className="list">
                {d.tasks.map((t) => {
                  const overdue = t.dueDate && new Date(t.dueDate) < new Date();
                  return (
                    <div key={t.id} className="list-item">
                      <button className="btn ghost icon" aria-label={`Mark "${t.title}" done`} onClick={() => completeTask(t.id)}>
                        <Circle />
                      </button>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="small" style={{ fontWeight: 500 }}>{t.title}</div>
                        <div className="tiny muted truncate">
                          {t.deal ? <Link to={`/projects/${t.deal.id}`}>{t.deal.name}</Link> : null}
                          {t.dueDate ? <span style={{ color: overdue ? 'var(--danger)' : undefined }}> · due {date(t.dueDate, { month: 'short', day: 'numeric' })}</span> : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <Empty icon={<CheckCircle2 />} title="You are all caught up" />
            )}
          </Card>

          {d.expiringRegistrations?.length ? (
            <Card title="Protection ending soon" flush>
              <div className="list">
                {d.expiringRegistrations.map((r) => (
                  <Link key={r.id} className="list-item" to={`/projects/${r.deal.id}`}>
                    <ShieldAlert size={18} color="var(--warn)" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="small truncate" style={{ fontWeight: 500 }}>{r.deal.name}</div>
                      <div className="tiny muted">Ends {relative(r.expiresAt)}</div>
                    </div>
                  </Link>
                ))}
              </div>
            </Card>
          ) : null}

          <Card title="Samples in motion" actions={<Link to="/samples" className="btn ghost sm">All samples</Link>} flush>
            {d.samples.length ? (
              <div className="list">
                {d.samples.map((s) => (
                  <Link key={s.id} className="list-item" to={`/projects/${s.deal.id}/samples`}>
                    <Package size={18} color="var(--emerald)" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="small truncate" style={{ fontWeight: 500 }}>{s.deal.name}</div>
                      <div className="tiny muted truncate">{s.trackingNumber ? `${s.carrier ?? 'Tracking'} ${s.trackingNumber}` : `Updated ${relative(s.updatedAt)}`}</div>
                    </div>
                    <StatusBadge map={SAMPLE_STATUS} value={s.status} />
                  </Link>
                ))}
              </div>
            ) : (
              <Empty title="No samples in transit">Request samples from a project's Samples tab.</Empty>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}

function StaffStat({ to, label, value, alert }: { to: string; label: string; value: number; alert?: string }) {
  return (
    <Link to={to} className={`card stat ${value ? 'accent' : ''}`} style={{ textDecoration: 'none' }}>
      <span className="value num">{value}</span>
      <span className="label">
        {label}
        {alert ? <span className="badge red" style={{ marginLeft: 6 }}>{alert}</span> : null}
      </span>
    </Link>
  );
}
